import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
const TARGET_MS_PER_CHAR = 15;
const MIN_CHARS_PER_FRAME = 1;
const MAX_SMOOTH_CHARS = 200;
const MAX_FRAME_MS = 50;
const DRAIN_ACCEL_THRESHOLD = 30;

/**
 * Smoothly reveals text character-by-character using requestAnimationFrame,
 * distributing chunky server deltas into a fluid typing animation.
 *
 * Speed adapts to the buffer size: when the LLM is producing text faster
 * than the base reveal rate, the animation gradually accelerates to keep
 * the buffer from hitting {@link MAX_SMOOTH_CHARS} (which snaps forward).
 *
 * When `streaming` is false the full text is returned immediately.
 *
 * Verbatim port of the private `@magnus/react` `useSmoothText` — the two
 * implementations must not drift (the flagship's replay-vs-live behavior
 * depends on the exact cadence/snap rules above).
 */
export function useSmoothText(text, streaming) {
  const $ = _c(6);
  const [displayed, setDisplayed] = useState(text);
  const raf = useRef(0);
  const cursor = useRef(text.length);
  const targetRef = useRef(text);
  const lastFrame = useRef(0);
  let t0;
  let t1;
  if ($[0] !== streaming || $[1] !== text) {
    t0 = () => {
      targetRef.current = text;
      if (!streaming) {
        cancelAnimationFrame(raf.current);
        raf.current = 0;
        lastFrame.current = 0;
        cursor.current = text.length;
        setDisplayed(text);
        return;
      }
      if (cursor.current > text.length) {
        cursor.current = text.length;
        setDisplayed(text);
        return;
      }
      if (text.length - cursor.current > MAX_SMOOTH_CHARS) {
        cursor.current = text.length;
        setDisplayed(text);
        return;
      }
      if (cursor.current >= text.length) {
        return;
      }
      if (raf.current) {
        return;
      }
      function tick(now) {
        const target = targetRef.current;
        const remaining = target.length - cursor.current;
        if (remaining > MAX_SMOOTH_CHARS) {
          cursor.current = target.length;
          setDisplayed(target);
          raf.current = 0;
          lastFrame.current = 0;
          return;
        }
        if (remaining <= 0) {
          raf.current = 0;
          lastFrame.current = 0;
          return;
        }
        const elapsed = Math.min(lastFrame.current ? now - lastFrame.current : 0, MAX_FRAME_MS);
        lastFrame.current = now;
        const base = Math.max(MIN_CHARS_PER_FRAME, Math.round(elapsed / TARGET_MS_PER_CHAR));
        const boost = remaining > DRAIN_ACCEL_THRESHOLD ? Math.ceil((remaining - DRAIN_ACCEL_THRESHOLD) / 12) : 0;
        const advance = Math.min(base + boost, remaining);
        cursor.current = cursor.current + advance;
        setDisplayed(target.slice(0, cursor.current));
        raf.current = requestAnimationFrame(tick);
      }
      raf.current = requestAnimationFrame(tick);
    };
    t1 = [text, streaming];
    $[0] = streaming;
    $[1] = text;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      cancelAnimationFrame(raf.current);
    };
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return displayed;
}